"use client";

import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Camera,
  ChevronLeft,
  ChevronRight,
  Clock,
  Gift as GiftIcon,
  Heart,
  Music2,
  Sparkles,
} from "lucide-react";
import type { ComponentType } from "react";
import type { Gift } from "@/types";
import { cn, occasionLabel } from "@/lib/utils";
import type { TemplateConfig } from "@/lib/templates";
import {
  getExperienceData,
  type CuriositiesExperienceData,
} from "@/data/experienceDataSchema";

// ─── Helpers — geração de Q&A a partir de dados REAIS do gift ──────────────
// Nunca inventamos fatos do casal: cada item só é incluído se a fonte de
// dados existir. Itens sem dado correspondente são silenciosamente omitidos.

interface CuriosityItem {
  id: string;
  icon: ComponentType<{ className?: string }>;
  question: string;
  answer: string;
}

function parseStartDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function capitalize(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatLongDate(date: Date): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

function formatWeekday(date: Date): string {
  try {
    const weekday = new Intl.DateTimeFormat("pt-BR", {
      weekday: "long",
    }).format(date);
    return capitalize(weekday);
  } catch {
    return "";
  }
}

// Curiosidades personalizadas pelo usuário sobrescrevem completamente os
// derivados do gift. Quando o usuário escreve sua própria curiosidade, ela
// vira a fonte da verdade da seção — caímos para o derivado só se a lista
// estiver vazia ou ausente.
function buildCustomCuriosityItems(
  data: CuriositiesExperienceData["items"]
): CuriosityItem[] | null {
  if (!Array.isArray(data) || data.length === 0) return null;
  const list: CuriosityItem[] = [];
  for (let i = 0; i < data.length; i++) {
    const item = data[i] ?? {};
    const question = item.question?.trim();
    const answer = item.answer?.trim();
    if (!question || !answer) continue;
    list.push({
      id: `custom-${i}`,
      icon: Sparkles,
      question,
      answer,
    });
  }
  return list.length > 0 ? list : null;
}

export function buildCuriosityItems(gift: Partial<Gift>): CuriosityItem[] {
  const items: CuriosityItem[] = [];
  const startDate = parseStartDate(gift.coupleStartDate);

  if (startDate) {
    items.push({
      id: "since",
      icon: Calendar,
      question: "Quando essa história começou?",
      answer: formatLongDate(startDate),
    });

    const diffMs = Date.now() - startDate.getTime();
    if (diffMs >= 0) {
      const totalDays = Math.floor(diffMs / 86_400_000);
      items.push({
        id: "days",
        icon: Heart,
        question: "Quantos dias caminhamos juntos até hoje?",
        answer: `${totalDays.toLocaleString("pt-BR")} ${
          totalDays === 1 ? "dia" : "dias"
        }`,
      });
    }

    items.push({
      id: "year",
      icon: Sparkles,
      question: "Em que ano tudo começou?",
      answer: String(startDate.getFullYear()),
    });

    const weekday = formatWeekday(startDate);
    if (weekday) {
      items.push({
        id: "weekday",
        icon: Clock,
        question: "Em que dia da semana essa história começou?",
        answer: weekday,
      });
    }
  }

  const music = gift.musicName?.trim();
  const artist = gift.artistName?.trim();
  if (music) {
    items.push({
      id: "music",
      icon: Music2,
      question: "Qual é a nossa trilha sonora?",
      answer: artist ? `${music} — ${artist}` : music,
    });
  }

  const photoCount = gift.photos?.length ?? 0;
  if (photoCount > 0) {
    items.push({
      id: "photos",
      icon: Camera,
      question: "Quantas memórias guardamos em fotos?",
      answer: `${photoCount} ${
        photoCount === 1 ? "foto eternizada" : "fotos eternizadas"
      }`,
    });
  }

  const eventCount = gift.timelineEvents?.length ?? 0;
  if (eventCount > 0) {
    items.push({
      id: "events",
      icon: GiftIcon,
      question: "Quantos marcos especiais já vivemos juntos?",
      answer: `${eventCount} ${
        eventCount === 1 ? "momento" : "momentos"
      } na linha do tempo`,
    });
  }

  if (gift.occasion) {
    items.push({
      id: "occasion",
      icon: Sparkles,
      question: "Qual ocasião estamos celebrando aqui?",
      answer: occasionLabel(gift.occasion),
    });
  }

  return items;
}

// ─── Componente interativo — alterna entre pergunta e resposta ─────────────

export type CuriositiesQuizVariant = "default" | "spotify";

interface QuizPalette {
  accent: string;
  accentRgb: string;
}

interface CuriositiesQuizProps {
  gift: Partial<Gift>;
  variant: CuriositiesQuizVariant;
  cfg?: TemplateConfig;
  palette?: QuizPalette;
}

export function CuriositiesQuiz({
  gift,
  variant,
  cfg,
  palette,
}: CuriositiesQuizProps) {
  const items = useMemo(() => {
    const customData = getExperienceData<CuriositiesExperienceData>(
      gift,
      "curiosities"
    );
    const custom = buildCustomCuriosityItems(customData?.items);
    if (custom) return custom;
    return buildCuriosityItems(gift);
  }, [gift]);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);

  if (items.length === 0) {
    if (variant === "spotify") {
      return (
        <p className="text-white/55 text-[11px]">
          Preencha mais detalhes do casal para revelar curiosidades.
        </p>
      );
    }
    return (
      <p className={cn("text-xs opacity-60", cfg?.bodyTextClass)}>
        Preencha mais detalhes do casal para revelar curiosidades.
      </p>
    );
  }

  const safeIndex = ((index % items.length) + items.length) % items.length;
  const current = items[safeIndex];
  const Icon = current.icon;

  function go(delta: number) {
    setRevealed(false);
    setIndex((idx) => idx + delta);
  }

  if (variant === "spotify") {
    const accent = palette?.accent ?? "#1DB954";
    const accentRgb = palette?.accentRgb ?? "29,185,84";
    return (
      <div className="curiosities-quiz curiosities-quiz--spotify flex flex-col gap-3">
        <div
          className="relative rounded-xl p-4 flex flex-col gap-3 min-h-[120px] overflow-hidden"
          style={{
            background: `linear-gradient(135deg, rgba(${accentRgb},0.16) 0%, rgba(${accentRgb},0.04) 100%)`,
            border: `1px solid rgba(${accentRgb},0.22)`,
          }}
        >
          <div className="flex items-center gap-2">
            <span
              className="inline-flex h-6 w-6 items-center justify-center rounded-full"
              style={{
                background: `rgba(${accentRgb},0.18)`,
                color: accent,
              }}
            >
              <Icon className="h-3 w-3" />
            </span>
            <span
              className="text-[9px] uppercase tracking-[0.18em] font-bold"
              style={{ color: accent }}
            >
              Curiosidade {safeIndex + 1}/{items.length}
            </span>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={`${current.id}-${revealed ? "a" : "q"}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22 }}
              className="flex flex-col gap-1.5"
            >
              {revealed ? (
                <>
                  <p className="text-[9px] uppercase tracking-[0.18em] text-white/45">
                    Resposta
                  </p>
                  <p
                    className="text-[14px] font-semibold leading-snug"
                    style={{ color: accent }}
                  >
                    {current.answer}
                  </p>
                </>
              ) : (
                <p className="text-[13px] font-medium text-white/95 leading-snug">
                  {current.question}
                </p>
              )}
            </motion.div>
          </AnimatePresence>

          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            aria-pressed={revealed}
            className="self-start rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors"
            style={{
              background: revealed ? `rgba(${accentRgb},0.16)` : accent,
              color: revealed ? accent : "#0F0F12",
            }}
          >
            {revealed ? "Esconder resposta" : "Ver resposta"}
          </button>
        </div>

        {items.length > 1 ? (
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Curiosidade anterior"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 text-white/70 transition-colors hover:bg-white/5"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <div className="flex items-center gap-1.5">
              {items.map((it, i) => (
                <span
                  key={it.id}
                  className="h-1.5 w-1.5 rounded-full transition-colors"
                  style={{
                    background:
                      i === safeIndex ? accent : `rgba(${accentRgb},0.25)`,
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Próxima curiosidade"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 text-white/70 transition-colors hover:bg-white/5"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  // ─── Variante default — clara, romântica, premium ──────────────────────
  const accent = cfg?.accentHex ?? "#E11D48";
  return (
    <div className="curiosities-quiz curiosities-quiz--default flex flex-col gap-4">
      <div
        className="relative rounded-2xl p-5 flex flex-col gap-3 min-h-[150px] overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${accent}14 0%, ${accent}05 100%)`,
          border: `1px solid ${accent}26`,
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="inline-flex h-8 w-8 items-center justify-center rounded-full"
            style={{ background: `${accent}1A`, color: accent }}
          >
            <Icon className="h-4 w-4" />
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.22em]"
            style={{ color: accent }}
          >
            Curiosidade {safeIndex + 1} de {items.length}
          </span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={`${current.id}-${revealed ? "a" : "q"}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="flex flex-col gap-1.5"
          >
            {revealed ? (
              <>
                <p
                  className={cn(
                    "text-[10px] uppercase tracking-[0.18em] opacity-60",
                    cfg?.bodyTextClass
                  )}
                >
                  Resposta
                </p>
                <p
                  className="text-lg font-semibold leading-snug"
                  style={{ color: accent }}
                >
                  {current.answer}
                </p>
              </>
            ) : (
              <p
                className={cn(
                  "text-base font-semibold leading-snug",
                  cfg?.bodyTextClass
                )}
              >
                {current.question}
              </p>
            )}
          </motion.div>
        </AnimatePresence>

        <button
          type="button"
          onClick={() => setRevealed((r) => !r)}
          aria-pressed={revealed}
          className="self-start mt-1 rounded-full px-4 py-1.5 text-xs font-semibold shadow-sm transition-colors"
          style={{
            background: revealed ? `${accent}1A` : accent,
            color: revealed ? accent : "#ffffff",
          }}
        >
          {revealed ? "Esconder resposta" : "Ver resposta"}
        </button>
      </div>

      {items.length > 1 ? (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Curiosidade anterior"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors hover:bg-black/[0.03]"
            style={{ borderColor: `${accent}40`, color: accent }}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {items.map((it, i) => (
              <span
                key={it.id}
                className="h-1.5 w-1.5 rounded-full transition-colors"
                style={{
                  background: i === safeIndex ? accent : `${accent}40`,
                }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Próxima curiosidade"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors hover:bg-black/[0.03]"
            style={{ borderColor: `${accent}40`, color: accent }}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
